import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Instagram, Facebook, Twitter } from 'lucide-react';
import { brandInfo } from '../../data/products';

export const Footer = ({ onOpenPolicy }) => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    shop: [
      { name: 'All Collections', path: '/category/all' },
      { name: 'Living Room', path: '/category/living-room' },
      { name: 'Bedroom', path: '/category/bedroom' },
      { name: 'Dining Room', path: '/category/dining' },
      { name: 'Office', path: '/category/office' },
    ],
    support: [
      { name: 'Contact Us', path: '/contact' },
      { name: 'About Us', path: '/about' },
      { name: 'Shipping Policy', action: () => onOpenPolicy('shipping') },
      { name: 'Returns & Refunds', action: () => onOpenPolicy('returns') },
    ],
    legal: [
      { name: 'Privacy Policy', action: () => onOpenPolicy('privacy') },
      { name: 'Terms & Conditions', action: () => onOpenPolicy('terms') },
    ]
  };

  return (
    <footer className="bg-[#2D4A3E] text-white" data-testid="footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Brand Column */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="font-['Cormorant_Garamond'] text-3xl font-semibold tracking-tight" data-testid="footer-logo">
              Bright Vista
            </Link>
            <p className="mt-4 text-[#A8B5AB] text-sm leading-relaxed font-['Montserrat']">
              Luxury furniture designed for modern living. Bringing elegance, comfort, and timeless style to your home.
            </p>
            <div className="flex gap-4 mt-6">
              <a href={brandInfo.socials.instagram} className="text-[#A8B5AB] hover:text-[#C67D4E] transition-colors" data-testid="social-instagram">
                <Instagram size={20} />
              </a>
              <a href={brandInfo.socials.facebook} className="text-[#A8B5AB] hover:text-[#C67D4E] transition-colors" data-testid="social-facebook">
                <Facebook size={20} />
              </a>
              <a href={brandInfo.socials.twitter} className="text-[#A8B5AB] hover:text-[#C67D4E] transition-colors" data-testid="social-twitter">
                <Twitter size={20} />
              </a>
            </div>
          </div>

          {/* Shop Links */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.15em] text-[#C67D4E] mb-5 font-['Montserrat'] font-semibold">Shop</h4>
            <ul className="space-y-3">
              {footerLinks.shop.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path} 
                    className="text-[#A8B5AB] hover:text-white transition-colors text-sm font-['Montserrat']"
                    data-testid={`footer-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.15em] text-[#C67D4E] mb-5 font-['Montserrat'] font-semibold">Support</h4>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  {link.path ? (
                    <Link 
                      to={link.path} 
                      className="text-[#A8B5AB] hover:text-white transition-colors text-sm font-['Montserrat']"
                      data-testid={`footer-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <button 
                      onClick={link.action}
                      className="text-[#A8B5AB] hover:text-white transition-colors text-sm font-['Montserrat']"
                      data-testid={`footer-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.15em] text-[#C67D4E] mb-5 font-['Montserrat'] font-semibold">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#C67D4E] flex-shrink-0 mt-0.5" />
                <span className="text-[#A8B5AB] text-sm font-['Montserrat']">{brandInfo.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-[#C67D4E] flex-shrink-0" />
                <a href={`tel:${brandInfo.phone}`} className="text-[#A8B5AB] hover:text-white transition-colors text-sm font-['Montserrat']">
                  {brandInfo.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-[#C67D4E] flex-shrink-0" />
                <a href={`mailto:${brandInfo.email}`} className="text-[#A8B5AB] hover:text-white transition-colors text-sm font-['Montserrat']">
                  {brandInfo.email}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#3D5A4E]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-5">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#A8B5AB] text-sm font-['Montserrat']">
              © {currentYear} Bright Vista. All rights reserved.
            </p>
            <div className="flex gap-6">
              {footerLinks.legal.map((link) => (
                <button
                  key={link.name}
                  onClick={link.action}
                  className="text-[#A8B5AB] hover:text-white transition-colors text-sm font-['Montserrat']"
                  data-testid={`footer-legal-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
